<template>
    <ActionSection class="mt-5 border-b border-gray-300 pb-5">
        <template #title> Passkeys </template>

        <template #description>
            Sign in with Touch ID, Face ID, Windows Hello or a security key
            instead of a password.
        </template>

        <template #content>
            <div class="text-sm text-gray-500">
                You can also turn on passkeys under Two Factor Authentication,
                so signing in with a password asks for a passkey as the second
                step.
            </div>

            <ul
                v-if="passkeys.length > 0"
                class="mt-5 divide-y divide-gray-200 rounded-[6px] border border-gray-200"
            >
                <li
                    v-for="passkey in passkeys"
                    :key="passkey.id"
                    class="flex items-center justify-between gap-4 p-4"
                >
                    <div class="flex min-w-0 items-center gap-3">
                        <KeyRound class="h-5 w-5 shrink-0 text-gray-400" />
                        <div class="min-w-0">
                            <div class="truncate text-sm font-medium text-gray-800">
                                {{ passkey.name }}
                                <span
                                    v-if="passkey.authenticator"
                                    class="font-normal text-gray-500"
                                >
                                    · {{ passkey.authenticator }}
                                </span>
                            </div>
                            <div class="text-xs text-gray-500">
                                {{ describePasskey(passkey) }}
                            </div>
                        </div>
                    </div>

                    <component
                        :is="confirmComponent"
                        :allow-passkey="passkeys.length > 0"
                        @confirmed="removePasskey(passkey)"
                    >
                        <AtomicButton
                            size="sm"
                            color="white"
                            :disabled="deletingId === passkey.id"
                        >
                            Remove
                        </AtomicButton>
                    </component>
                </li>
            </ul>

            <p v-if="!isSupported" class="mt-5 text-sm text-gray-500">
                This browser doesn't support passkeys.
            </p>
        </template>

        <template #action>
            <div v-if="isSupported" class="flex items-center justify-end">
                <component
                    :is="confirmComponent"
                    :allow-passkey="passkeys.length > 0"
                    @confirmed="addPasskey"
                >
                    <AtomicButton size="sm" color="yellow" :disabled="isLoading">
                        Add passkey
                    </AtomicButton>
                </component>
            </div>
        </template>
    </ActionSection>
</template>

<script lang="ts" setup>
import { router } from '@inertiajs/vue3';
import { UserCancelledError } from '@laravel/passkeys';
import { usePasskeyRegister } from '@laravel/passkeys/vue';
import { KeyRound } from 'lucide-vue-next';
import { route } from 'ziggy-js';
import IPasskey from '@/types/Settings/IPasskey';
import useConfirm from '@/composables/useConfirm';
import { create } from '@/composables/useNotifications';
import { describePasskey } from '@/utils/passkeys';
import ConfirmsPassword from './ConfirmsPassword.vue';
import ConfirmsOtp from './ConfirmsOtp.vue';

const props = defineProps<{
    passkeys: IPasskey[];
    hasPassword: boolean;
}>();

const { show } = useConfirm();
const deletingId = ref<number | null>(null);

const confirmComponent = computed(() =>
    props.hasPassword ? ConfirmsPassword : ConfirmsOtp,
);

const { register, isLoading, isSupported } = usePasskeyRegister({
    onSuccess: () => {
        create({ title: 'Passkey added', type: 'success' });
        router.reload({ only: ['passkeys'] });
    },
    onError: (error) => {
        if (error instanceof UserCancelledError) {
            return;
        }

        create({
            title: error.message || 'Could not add the passkey.',
            type: 'error',
        });
    },
});

const addPasskey = async () => {
    const name = await show({
        header: 'Add a passkey',
        message:
            'Give this passkey a name so you can recognise it later, then follow your browser’s prompt.',
        confirm: 'Continue',
        hasInput: true,
        inputData: {
            label: 'Name',
            placeholder: 'e.g. MacBook Pro',
            validator: (text: string) => text.trim().length > 0,
            validationErrorMessage: 'Please enter a name.',
        },
    });

    if (typeof name !== 'string') {
        return;
    }

    await register(name.trim());
};

const removePasskey = async (passkey: IPasskey) => {
    const confirmed = await show({
        header: 'Remove passkey',
        message: `You will no longer be able to sign in with “${passkey.name}”. It stays saved on your device or password manager until you delete it there too.`,
        confirm: 'Remove',
        type: 'danger',
    });

    if (!confirmed) {
        return;
    }

    deletingId.value = passkey.id;

    router.delete(route('passkey.destroy', passkey.id), {
        preserveScroll: true,
        // Removing the last passkey also turns off passkey 2FA, which lives on the user
        only: ['passkeys', 'user'],
        onSuccess: () => create({ title: 'Passkey removed', type: 'success' }),
        onError: (errors) =>
            create({
                title:
                    (Object.values(errors)[0] as string) ||
                    'Could not remove the passkey.',
                type: 'error',
            }),
        onFinish: () => {
            deletingId.value = null;
        },
    });
};
</script>
